// Shared circular service for managing circulars across dashboard and website
import { Circular } from './api';

// Re-export the Circular interface for components that need it
export type { Circular };

class CircularService {
  private baseUrl = 'https://localhost:7222/api/Circular';
  private circulars: Circular[] = [];
  private listeners: Array<() => void> = [];

  // Get all circulars from API
  async getCirculars(): Promise<Circular[]> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.circulars = data;
      return this.circulars.filter(circular => circular.bolisActive);
    } catch (error) {
      console.error('Error fetching circulars:', error);
      // Return empty array if API fails
      return [];
    }
  }

  // Add a new circular via API
  async addCircular(circularDate: string, issuedBy: string, refNo: string, subject: string, file: File, bolisActive: boolean = true): Promise<Circular> {
    try {
      const token = localStorage.getItem('token');

      // Validate inputs
      if (!subject || !file) {
        throw new Error('Subject and file are required');
      }

      // Check file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        throw new Error('File size must be less than 100MB');
      }

      const formData = new FormData();
      formData.append('circularDate', circularDate);
      formData.append('issuedBy', issuedBy);
      formData.append('refNo', refNo);
      formData.append('subject', subject);
      formData.append('file', file);
      formData.append('bolisActive', bolisActive.toString());

      const headers: HeadersInit = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorText = await response.text();
          console.error('Error response body:', errorText);
          
          // Try to parse as JSON for better error messages
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage += `, message: ${errorJson.message || errorJson.error || errorText}`;
          } catch {
            errorMessage += `, message: ${errorText}`;
          }
        } catch (e) {
          errorMessage += ', unable to read error response';
        }
        
        throw new Error(errorMessage);
      }

      const newCircular = await response.json();
      this.circulars.unshift(newCircular);
      this.notifyListeners();
      return newCircular;
    } catch (error) {
      console.error('Error adding circular:', error);
      throw error;
    }
  }

  // Delete a circular via API
  async deleteCircular(id: string): Promise<boolean> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const index = this.circulars.findIndex(c => c.strGUID === id);
      if (index !== -1) {
        this.circulars.splice(index, 1);
        this.notifyListeners();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting circular:', error);
      throw error;
    }
  }

  // Update circular via API
  async updateCircular(id: string, circularDate: string, issuedBy: string, refNo: string, subject: string, file?: File): Promise<Circular> {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('circularDate', circularDate);
      formData.append('issuedBy', issuedBy);
      formData.append('refNo', refNo);
      formData.append('subject', subject);
      if (file) {
        formData.append('file', file);
      }

      const headers: HeadersInit = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers,
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCircular = await response.json();
      const index = this.circulars.findIndex(c => c.strGUID === id);
      if (index !== -1) {
        this.circulars[index] = updatedCircular;
        this.notifyListeners();
      }
      return updatedCircular;
    } catch (error) {
      console.error('Error updating circular:', error);
      throw error;
    }
  }

  // Download circular via API
  async downloadCircular(id: string): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${this.baseUrl}/${id}/download`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `circular-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading circular:', error);
      throw error;
    }
  }

  // Subscribe to changes
  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  async showFile(id: string): Promise<string> {
    // This would typically open the file in a new tab or modal
    return `${this.baseUrl}/${id}/download`;
  }

  async downloadFile(id: string): Promise<void> {
    return this.downloadCircular(id);
  }
}

export const circularService = new CircularService(); 